import { PlacementRuleType } from '../../../api/generated';
import type { EditorDependency, EditorNode } from './types';

/** Remove item from its chain, repairing the successor's after-dep */
export function disconnectItem(all: EditorNode[], itemId: string): EditorNode[] {
  const item = all.find((wi) => wi.id === itemId);
  if (!item) {
    return all;
  }
  const predId = item.dependencies.find(
    (d) =>
      (d.type === PlacementRuleType.After || d.type === PlacementRuleType.ImmediatelyAfter) &&
      d.targetId
  )?.targetId;

  return all.map((wi) => {
    if (wi.id === itemId) return { ...wi, dependencies: [] };
    let changed = false;
    const updated = wi.dependencies
      .map((d) => {
        if (d.targetId !== itemId) return d;
        changed = true; // a dep referencing the removed item is repaired or dropped
        if (d.type === PlacementRuleType.After || d.type === PlacementRuleType.ImmediatelyAfter)
          return predId ? { ...d, targetId: predId } : null;
        return null;
      })
      .filter(Boolean) as EditorDependency[];
    return changed ? { ...wi, dependencies: updated } : wi;
  });
}

/**
 * Delete item from the group. Runs disconnectItem first so deps on other nodes
 * that referenced it are repaired (after-deps repoint to its predecessor) or
 * dropped, never left dangling on a node that no longer exists.
 */
export function removeNode(all: EditorNode[], itemId: string): EditorNode[] {
  return disconnectItem(all, itemId).filter((wi) => wi.id !== itemId);
}

/** Insert item between prevId and nextId, creating after/before deps */
export function insertBetween(
  all: EditorNode[],
  itemId: string,
  prevId: string | undefined,
  nextId: string | undefined
): EditorNode[] {
  return all.map((wi) => {
    if (wi.id === itemId) {
      const deps: EditorDependency[] = [];
      if (prevId)
        deps.push({ id: crypto.randomUUID(), type: PlacementRuleType.After, targetId: prevId });
      if (nextId)
        deps.push({ id: crypto.randomUUID(), type: PlacementRuleType.Before, targetId: nextId });
      return { ...wi, dependencies: deps };
    }
    // Update the next item's primary after-dep to point to the inserted item
    if (nextId && wi.id === nextId) {
      const updated = wi.dependencies.map((d) =>
        (d.type === PlacementRuleType.After || d.type === PlacementRuleType.ImmediatelyAfter) &&
        d.targetId === prevId
          ? { ...d, targetId: itemId }
          : d
      );
      const hasAfterInserted = updated.some(
        (d) =>
          (d.type === PlacementRuleType.After || d.type === PlacementRuleType.ImmediatelyAfter) &&
          d.targetId === itemId
      );
      if (!hasAfterInserted) {
        return {
          ...wi,
          dependencies: [
            ...updated,
            { id: crypto.randomUUID(), type: PlacementRuleType.After, targetId: itemId }
          ]
        };
      }
      return { ...wi, dependencies: updated };
    }
    return wi;
  });
}

/** Reorder items within a chain, updating primary after-deps */
const isOrderingDep = (type: PlacementRuleType): boolean =>
  type === PlacementRuleType.After ||
  type === PlacementRuleType.ImmediatelyAfter ||
  type === PlacementRuleType.Before ||
  type === PlacementRuleType.ImmediatelyBefore;

const isAfterDep = (type: PlacementRuleType): boolean =>
  type === PlacementRuleType.After || type === PlacementRuleType.ImmediatelyAfter;

export function reorderChain(
  all: EditorNode[],
  chainItems: EditorNode[],
  newOrder: EditorNode[]
): EditorNode[] {
  const chainIds = new Set(chainItems.map((wi) => wi.id));

  // The chain's external entry: the current head's after/immediately-after rule
  // that points OUT of the chain (e.g. a branch row that starts "after" a node
  // on another row). On a reorder it must follow the head to whichever node
  // becomes the new head — otherwise it strands on the old head, leaving that
  // node with a duplicate "after" rule and the new head with none.
  const oldHead = chainItems[0];
  const entryDep = oldHead?.dependencies.find(
    (d) => isAfterDep(d.type) && d.targetId && !chainIds.has(d.targetId)
  );
  const newHeadId = newOrder[0]?.id;

  return all.map((wi) => {
    if (!chainIds.has(wi.id)) return wi;
    const newIdx = newOrder.findIndex((item) => item.id === wi.id);
    const newPredId = newIdx > 0 ? newOrder[newIdx - 1].id : undefined;
    // Keep the existing in-chain "after" rule's type so an "immediately after"
    // link survives a reorder.
    const primary = wi.dependencies.find(
      (d) => isAfterDep(d.type) && d.targetId && chainIds.has(d.targetId)
    );
    // Drop every ordering rule that points at another node in this chain
    // (including a stale "before" link) and the chain's external entry rule (it
    // is re-attached to the new head below), so a reorder can never leave a node
    // with duplicate or contradictory order. Other cross-chain and anchor rules
    // (first/last) are preserved.
    const rest = wi.dependencies.filter(
      (d) => !(isOrderingDep(d.type) && d.targetId && chainIds.has(d.targetId)) && d !== entryDep
    );
    const deps = [...rest];
    if (newPredId) {
      deps.push(
        primary
          ? { ...primary, targetId: newPredId }
          : { id: crypto.randomUUID(), type: PlacementRuleType.After, targetId: newPredId }
      );
    } else if (entryDep && wi.id === newHeadId) {
      // New head inherits the chain's external entry link.
      deps.push({ ...entryDep });
    }
    return { ...wi, dependencies: deps };
  });
}
